/**
 * <UserLabel value={emailOrId}/> — thin display wrapper for user
 * audit fields (`created_by`, `approved_by`, `posted_by`, etc.).
 *
 * Sprint 2026-Feb-10n. Resolves the incoming email/id to the user's
 * display name via the global `UserDirectory` cache. Falls back to the
 * raw stored value when no mapping is found — so historic audit rows
 * (deleted users, system events) never lose information.
 *
 * Usage:
 *   <UserLabel value={doc.approved_by} />
 *   <UserLabel value={doc.approved_by} withEmail />       // "Name (email)"
 *   <UserLabel value={doc.approved_by} fallback="System" />
 *
 * The hook `useUserDirectory()` triggers a one-time fetch on first
 * mount + subscribes to cache refreshes so name changes propagate
 * across the app without a page reload.
 */
import React, { useEffect, useState } from "react";
import {
  ensureUserDirectory,
  subscribeUserDirectory,
  formatUser,
  resolveUser,
} from "../lib/userDirectory";


/** Hook — hydrates the directory once and re-renders when it refreshes. */
export function useUserDirectory() {
  const [, setTick] = useState(0);
  useEffect(() => {
    let mounted = true;
    ensureUserDirectory().then(() => { if (mounted) setTick(t => t + 1); });
    const unsub = subscribeUserDirectory(() => { if (mounted) setTick(t => t + 1); });
    return () => { mounted = false; unsub(); };
  }, []);
}


/** Display component — pure text (no wrapper element) so it drops
 *  into any parent (`<td>`, `<span>`, tooltip, badge) without breaking
 *  layout. */
export default function UserLabel({
  value,
  fallback = "—",
  withEmail = false,
  className = "",
  testid,
}) {
  useUserDirectory();
  if (value === null || value === undefined || value === "") {
    return <span className={className} data-testid={testid}>{fallback}</span>;
  }
  if (withEmail) {
    const { name, email } = resolveUser(value);
    if (email && name && name.toLowerCase() !== email.toLowerCase()) {
      return (
        <span className={className} data-testid={testid} title={email}>
          {name}
          <span className="ml-1 text-[10px] text-slate-400">({email})</span>
        </span>
      );
    }
    return <span className={className} data-testid={testid} title={String(value)}>{name || value}</span>;
  }
  const name = formatUser(value, fallback);
  return (
    <span className={className} data-testid={testid} title={String(value)}>
      {name}
    </span>
  );
}


/** String-context helper — for places where JSX is inconvenient
 *  (e.g. Excel exports, notification toasts, template literals). */
export { formatUser };
